import type { BriefStatus, TaskStatus } from '@dyspatch/core';
import {
  CheckCircle2,
  CircleDashed,
  CircleDot,
  CircleSlash,
  Clock,
  FileText,
  Loader,
  Play,
  Send,
  ShieldCheck,
  Upload,
  XCircle,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * One icon per state, shape-distinct rather than colour-distinct: colour alone
 * does not survive a greyscale read, and a person scanning forty rows is reading
 * shapes long before they are reading hues.
 *
 * The tones map onto the semantic tokens rather than onto colours, so the accent
 * lands on exactly two states -- `review` and `awaiting_approval` -- and those
 * are the two where a human must act. `running` and `reporting` wear the machine
 * hue; everything settled is foreground or nothing.
 *
 * `running` carries one more thing a shape and a colour cannot: a step that has
 * been running for forty minutes has to look different from one that died
 * mid-flight, and a step list can hold hundreds of these icons at once. `dy-alive`
 * (index.css) is a ring that pulses off the icon's own colour using only
 * `transform` and `opacity`, so a page with two hundred rows and a dozen of them
 * `running` never asks the main thread to repaint. `prefers-reduced-motion`
 * strips the pulse but leaves the ring itself standing, because dropping the
 * whole cue would leave the one reader who turned off motion with no way at all
 * to tell a running step from a stopped one.
 *
 * This table, and only this table, decides a state's icon, colour and word
 * (issue #65). Before this, the same idea was drawn at whatever size the
 * surface that needed it reached for -- `size-4` in the job row, `size-[13px]`
 * in the plan list, `size-3.5` on a board card, `size-[13px]` again in the
 * graph -- and at 13px the queued clock and the done tick were a few pixels of
 * grey apart. `STATE_ICON_SIZE` is the one size every surface draws at now, and
 * `TaskIcon`/`BriefIcon` put it after `className` in the class list so a
 * surface cannot quietly shrink or grow it back: `cn` is `tailwind-merge`
 * underneath, which resolves a conflicting utility by which one comes last, and
 * the size in this file is always last. `taskLook`/`briefLook` hand the same
 * icon, tone and word to anything that is not drawing through `TaskIcon`/
 * `BriefIcon` themselves -- the About page's legend, and
 * `packages/hub/test/state-vocabulary.test.ts` -- so the legend and the pin
 * cannot drift from what a row actually draws.
 */
export interface Look {
  icon: ComponentType<{ className?: string }>;
  tone: string;
  label: string;
}

/**
 * 16px: the smallest size at which the ten shapes above hold apart from a
 * normal reading distance without a heavier stroke than lucide draws them at.
 * Every surface that draws a state icon uses this -- see the note above.
 */
const STATE_ICON_SIZE = 'size-4';

const TASK_LOOK: Record<TaskStatus, Look> = {
  draft: { icon: FileText, tone: 'text-fg-3', label: 'draft' },
  queued: { icon: Clock, tone: 'text-fg-2', label: 'queued' },
  offered: { icon: Send, tone: 'text-fg-2', label: 'offered' },
  claimed: { icon: CircleDot, tone: 'text-fg-2', label: 'claimed' },
  running: { icon: Play, tone: 'text-info', label: 'running' },
  reporting: { icon: Upload, tone: 'text-info', label: 'reporting' },
  review: { icon: ShieldCheck, tone: 'text-gate', label: 'waiting on you' },
  done: { icon: CheckCircle2, tone: 'text-ok', label: 'done' },
  failed: { icon: XCircle, tone: 'text-bad', label: 'failed' },
  cancelled: { icon: CircleSlash, tone: 'text-fg-3', label: 'cancelled' },
};

const BRIEF_LOOK: Record<BriefStatus, Look> = {
  draft: { icon: FileText, tone: 'text-fg-3', label: 'draft' },
  decomposing: { icon: Loader, tone: 'text-plan', label: 'decomposing' },
  awaiting_approval: {
    icon: ShieldCheck,
    tone: 'text-gate',
    label: 'awaiting approval',
  },
  approved: { icon: CircleDashed, tone: 'text-plan', label: 'approved' },
  rejected: { icon: XCircle, tone: 'text-bad', label: 'rejected' },
  done: { icon: CheckCircle2, tone: 'text-ok', label: 'done' },
  failed: { icon: XCircle, tone: 'text-bad', label: 'failed' },
};

export function TaskIcon({ status, className }: { status: TaskStatus; className?: string }) {
  const look = TASK_LOOK[status] ?? TASK_LOOK.queued;
  const Icon = look.icon;
  const icon = (
    <Icon
      className={cn(className, STATE_ICON_SIZE, 'shrink-0', look.tone)}
      aria-label={look.label}
    />
  );
  if (status !== 'running') return icon;
  // The ring rides a wrapping span rather than the icon's own `::after`: an
  // `<svg>` is a replaced element in some engines, and generated content on a
  // replaced element does not reliably paint.
  return <span className={cn('dy-alive relative inline-flex shrink-0', look.tone)}>{icon}</span>;
}

export function BriefIcon({ status, className }: { status: BriefStatus; className?: string }) {
  const look = BRIEF_LOOK[status] ?? BRIEF_LOOK.draft;
  const Icon = look.icon;
  return (
    <Icon
      className={cn(className, STATE_ICON_SIZE, 'shrink-0', look.tone)}
      aria-label={look.label}
    />
  );
}

export function taskTone(status: TaskStatus): string {
  return (TASK_LOOK[status] ?? TASK_LOOK.queued).tone;
}

export function briefTone(status: BriefStatus): string {
  return (BRIEF_LOOK[status] ?? BRIEF_LOOK.draft).tone;
}

/** The icon, tone and word for a task status, for anything that draws the
 * look without going through `TaskIcon` itself -- the About page's legend and
 * this file's own pin test. */
export function taskLook(status: TaskStatus): Look {
  return TASK_LOOK[status] ?? TASK_LOOK.queued;
}

/** Same as `taskLook`, for a brief status. */
export function briefLook(status: BriefStatus): Look {
  return BRIEF_LOOK[status] ?? BRIEF_LOOK.draft;
}

/**
 * Which `Badge` variant reads as the same tone a state's own icon and text
 * wear (issue #64). One table rather than a `cn()` ternary at each call site,
 * so a state chip and that state's icon can never quietly disagree on tone.
 */
const TONE_VARIANT: Record<string, BadgeVariant> = {
  'text-fg-3': 'secondary',
  'text-fg-2': 'secondary',
  'text-info': 'info',
  'text-gate': 'gate',
  'text-ok': 'success',
  'text-bad': 'destructive',
  'text-plan': 'plan',
};

/** The `Badge` variant for a tone this file hands out (`taskLook`/`briefLook`). */
export function badgeVariantForTone(tone: string): BadgeVariant {
  return TONE_VARIANT[tone] ?? 'secondary';
}

/**
 * A task's state as a chip: the same icon, tone and word `TaskIcon` draws,
 * small enough to sit in a row of chips rather than standing alone (issue
 * #64). It reads `taskLook` directly rather than rendering through `TaskIcon`
 * itself, the same way `PausedChip` already draws its own icon inside a
 * `Badge` - a chip's icon is always smaller than the 16px every bare state
 * icon holds to, so wrapping `TaskIcon` here would only fight its own size
 * guard (see `STATE_ICON_SIZE` above and `state-vocabulary.test.ts`).
 */
export function TaskStateChip({ status, className }: { status: TaskStatus; className?: string }) {
  const look = taskLook(status);
  const Icon = look.icon;
  return (
    <Badge variant={badgeVariantForTone(look.tone)} className={className}>
      <Icon className="size-2.5" aria-hidden />
      {look.label}
    </Badge>
  );
}

/** Same as `TaskStateChip`, for a job. */
export function BriefStateChip({ status, className }: { status: BriefStatus; className?: string }) {
  const look = briefLook(status);
  const Icon = look.icon;
  return (
    <Badge variant={badgeVariantForTone(look.tone)} className={className}>
      <Icon className="size-2.5" aria-hidden />
      {look.label}
    </Badge>
  );
}
